import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Link } from "react-router";
import { Badge, Button, Card, ErrorText, Input, PageHeader, Spinner } from "../components/ui.tsx";
import { useBrand } from "../hooks.ts";

type Toolkit = { slug: string; name: string; logo: string | null; description: string };

export function ConnectionsPage() {
  const qc = useQueryClient();
  const brand = useBrand();
  const q = useQuery({ queryKey: ["connections"], queryFn: () => unwrap(api.me.connections.$get()) });
  const [search, setSearch] = useState("");
  const [justConnected] = useState(() => new URLSearchParams(location.search).get("connected"));
  useEffect(() => {
    if (justConnected) history.replaceState(null, "", "/connections");
  }, [justConnected]);

  const found = useQuery({
    queryKey: ["toolkit-search", search],
    queryFn: () => unwrap(api.me.connections.toolkits.$get({ query: { q: search } })),
    enabled: !!q.data?.enabled && q.data.allToolkits,
  });
  const connect = useMutation({
    mutationFn: (toolkit: string) => unwrap(api.me.connections.connect.$post({ json: { toolkit } })),
    onSuccess: (r) => {
      location.href = r.redirectUrl;
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.me.connections[":id"].$delete({ param: { id } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["connections"] }),
  });

  const catalogue = useMemo<Toolkit[]>(() => {
    if (!q.data?.enabled) return [];
    const base = q.data.toolkits.filter((t) => t.allowed);
    if (!q.data.allToolkits) return base;
    const extra = (found.data?.toolkits ?? []).filter((t) => !base.some((b) => b.slug === t.slug));
    return [...base, ...extra];
  }, [q.data, found.data]);

  const chat = useQuery({ queryKey: ["my-messaging"], queryFn: () => unwrap(api.me.messaging.$get()) });
  const unlink = useMutation({
    mutationFn: (id: string) => unwrap(api.me.messaging[":id"].$delete({ param: { id } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-messaging"] }),
  });

  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const data = q.data;

  return (
    <>
      <PageHeader title="Connected apps" description="Let your assistant work in apps like Gmail, GitHub or Google Drive on your behalf. Only you and your assistant can use your connections." />
      {!!chat.data?.available.length && (
        <>
          <h2 className="mb-3 text-base font-semibold">Chat with your assistant from</h2>
          <Card className="mb-8 divide-y divide-[var(--line)]">
            {chat.data.available.map((a) => {
              const linked = chat.data.identities.filter((i) => i.platform === a.platform);
              const name = { slack: "Slack", discord: "Discord", whatsapp: "WhatsApp" }[a.platform];
              return (
                <div key={a.platform} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5">
                  <div className="w-24 font-medium">{name}</div>
                  <div className="min-w-0 flex-1 text-sm text-[var(--muted)]">
                    {linked.length
                      ? linked.map((i) => (
                          <span key={i.id} className="mr-3 inline-flex items-center gap-2">
                            <Badge tone="good">linked</Badge> {i.displayName}
                            <button className="text-xs hover:text-red-700" onClick={() => confirm(`Disconnect ${name} account ${i.displayName}?`) && unlink.mutate(i.id)}>unlink</button>
                          </span>
                        ))
                      : a.platform === "slack"
                        ? <>Send a direct message to <b className="text-[var(--text)]">@{a.handle ?? brand.name}</b> — you're recognised by your Slack email.</>
                        : <>Message <b className="text-[var(--text)]">{a.handle ?? `the ${brand.name} bot`}</b> and send <b className="text-[var(--text)]">link</b> to connect.</>}
                  </div>
                </div>
              );
            })}
            <div className="px-5 py-3 text-xs text-[var(--muted)]">
              Want to see each step while your assistant works? Turn on live progress in <Link to="/settings" className="font-medium text-[var(--text)] underline">Settings</Link>.
            </div>
          </Card>
        </>
      )}
      {!data.enabled ? (
        <Card className="p-6 text-sm text-[var(--muted)]">Connected apps aren't enabled for your group. Ask an admin if you need them.</Card>
      ) : (
        <>
          {justConnected && (
            <Card className="mb-4 border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800">
              Finished connecting {data.toolkits.find((t) => t.slug === justConnected)?.name ?? justConnected}. It can take a few seconds to show as active.
            </Card>
          )}
          <h2 className="mb-3 text-base font-semibold">Your connections</h2>
          <Card className="mb-8 divide-y divide-[var(--line)]">
            {data.accounts.length === 0 && <p className="px-5 py-8 text-center text-sm text-[var(--muted)]">Nothing connected yet.</p>}
            {data.accounts.map((a) => {
              const t = data.toolkits.find((x) => x.slug === a.toolkit);
              return (
                <div key={a.id} className="flex items-center gap-4 px-5 py-3.5">
                  <Logo t={t ?? { slug: a.toolkit, name: a.toolkit, logo: null, description: "" }} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{t?.name ?? a.toolkit}</div>
                    <div className="text-xs text-[var(--muted)]">{a.createdAt ? `Connected ${new Date(a.createdAt).toLocaleDateString()}` : ""}</div>
                  </div>
                  <Badge tone={a.status === "ACTIVE" ? "good" : a.status === "FAILED" || a.status === "EXPIRED" ? "bad" : "neutral"}>{a.status.toLowerCase()}</Badge>
                  <Button variant="ghost" className="h-8" disabled={remove.isPending} onClick={() => confirm(`Disconnect ${t?.name ?? a.toolkit}? Your assistant loses access immediately.`) && remove.mutate(a.id)}>
                    Disconnect
                  </Button>
                </div>
              );
            })}
          </Card>

          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold">Add a connection</h2>
            {data.allToolkits && <Input placeholder="Search 1,500+ apps" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />}
          </div>
          <ErrorText error={connect.error ?? remove.error} />
          <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {catalogue.map((t) => (
              <Card key={t.slug} className="flex items-start gap-3 p-4">
                <Logo t={t} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{t.name}</div>
                  <p className="line-clamp-2 text-xs text-[var(--muted)]">{t.description}</p>
                </div>
                <Button className="h-8 shrink-0" disabled={connect.isPending} onClick={() => connect.mutate(t.slug)}>
                  {connect.isPending && connect.variables === t.slug ? <Spinner /> : "Connect"}
                </Button>
              </Card>
            ))}
            {data.allToolkits && found.isFetching && <div className="grid place-items-center py-6"><Spinner /></div>}
          </div>
        </>
      )}
    </>
  );
}

function Logo({ t }: { t: Toolkit }) {
  return t.logo ? (
    <img src={t.logo} alt="" className="h-9 w-9 shrink-0 rounded-lg border border-[var(--line)] bg-white object-contain p-1" />
  ) : (
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--panel-2)] text-sm font-semibold uppercase">{t.name[0]}</div>
  );
}
